// Critical Notifier Service
// Provides evaluation logic for critical success/failure and emits a window event

// Messages can be extended or localized later
export const SUCCESS_MESSAGES = [
  "Critical Success! You're unstoppable!",
  'A perfect strike — Critical Success!',
  "Luck's on your side — Critical Hit!",
  'Fortune smiles upon you — Critical Success!',
  "That'll be talked about in taverns — Critical Success!",
  'A devastating blow — you nailed it!',
  'Natural 20! The crowd goes wild!',
  "That's a textbook critical — excellent!",
  'Your strike landed true — tremendous!',
  'A moment of glory — critical hit!'
]

export const FAILURE_MESSAGES = [
  'Ouch — Critical Failure! Maybe try a different tactic.',
  "That's gonna sting — Critical Failure!",
  'Oh no! Critical Failure — the dice frown upon you.',
  'Give it up? Not yet! But that was rough.',
  'The gods are merciless today — Critical Failure!',
  'A catastrophic slip — that was brutal.',
  'Natural 1 — the worst possible outcome.',
  'The dice betrayed you — regroup and try again.',
  'A painful misfortune — lessons learned.',
  'Critical miss — the plan falls apart.'
]

// Double critical messages (two successive criticals)
export const DOUBLE_SUCCESS_MESSAGES = [
  'Double Critical! The stars align for you!',
  "Back-to-back criticals — that's legendary!",
  'Two in a row — unstoppable momentum!',
  'A double triumph — impossible luck!',
  'Twice blessed — may the fates favor you!',
  'Consecutive krits — the enemy trembles!',
  'Two perfect strikes — surgical precision!',
  'Double nat20 — history in the making!',
  'Momentum overload — nothing can stop you now!',
  'This is a run of miracles — well played!'
]

export const DOUBLE_FAILURE_MESSAGES = [
  'Double Failure — that hurts twice as much!',
  'Back-to-back critical failures — brutal!',
  'Two critical fails in a row — take a calming breath.',
  'Double nat1 — ouch, that stings!',
  'Consecutive disasters — maybe switch tactics.',
  'A rough patch — shake it off and try again.',
  'Twice the failure — time for a coffee break.',
  'The dice conspire — double misfortune!',
  'Two misses back-to-back — regroup and adapt.',
  'That streak needs to end — breathe and reset.'
]

// Triple critical messages (three successive criticals)
export const SUCCESS_STREAK_MESSAGES = [
  'Unreal — three criticals in a row! Legendary!',
  'Is this an instant kill? That was devastating!',
  'Trinity of triumphs — the bards will sing of this!',
  'Three back-to-back criticals — sheer domination!',
  'A hat-trick of criticals — utterly unstoppable!',
  'Historic roll — your enemies are doomed!',
  'Three perfect strikes — a legend is born!',
  'A triumvirate of triumph — wow!',
  'Streak of excellence — the room applauds!',
  'An epic run — tell your friends!'
]

export const FAILURE_STREAK_MESSAGES = [
  'Catastrophic — three critical failures in a row! Maybe take a break.',
  'Is this instant death? That was a brutal streak.',
  'Three fails in a row — fate is not kind today.',
  'A tragic trifecta of misses — regroup and try again.',
  'Triple nat1 — a painful unlucky trifecta.',
  'Unfortunate streak — consider a new approach.',
  'Three disasters — that was rough on everyone.',
  'A hard run of misses — take five and retry.',
  'Trio of failure — destiny has other plans.',
  'Brutal trifecta — morale check recommended.'
]

// Module-level debug controls (development only)
export let debugMode = false
export let debugResult = 'critical_failure_streak'

// Expose a simple global flag so other modules (store/UI) can suppress notifier logs
// without creating circular imports. Default mirrors `debugMode`.
try {
  if (typeof window !== 'undefined') window.__CRITICAL_NOTIFIER_DEBUG__ = !!debugMode
} catch {
  /* ignore */
}

export function setDebugMode(enabled) {
  debugMode = !!enabled
  try {
    if (typeof window !== 'undefined') window.__CRITICAL_NOTIFIER_DEBUG__ = !!debugMode
  } catch {
    /* ignore */
  }
}

export function setDebugResult(result) {
  debugResult = String(result)
}

// Debug control: use exported `setDebugMode` and `setDebugResult` in code.
// Avoid exposing debug toggles on `window` — control debug through module API only.

// Local console wrapper: shadow global `console` inside this module so all
// console calls here respect `debugMode`. This avoids changing global console.
const _globalConsole =
  typeof window !== 'undefined' && window.console
    ? window.console
    : typeof console !== 'undefined'
      ? console
      : { log: () => {}, warn: () => {} }
const console = {
  log: (...args) => {
    if (debugMode && _globalConsole && typeof _globalConsole.log === 'function')
      _globalConsole.log.apply(_globalConsole, args)
  },
  warn: (...args) => {
    if (debugMode && _globalConsole && typeof _globalConsole.warn === 'function')
      _globalConsole.warn.apply(_globalConsole, args)
  }
}

// Helper to parse die type like 'd20' or a number of sides
const getSidesFromDieType = (dieType) => {
  if (!dieType) return null
  if (typeof dieType === 'number') return dieType
  if (typeof dieType === 'string') {
    const m = dieType.match(/d(\d+)/i)
    if (m) return parseInt(m[1], 10)
    const n = parseInt(dieType, 10)
    if (!Number.isNaN(n)) return n
  }
  // If object with sides
  if (typeof dieType === 'object' && dieType.sides) return Number(dieType.sides)
  return null
}

// Evaluate a single roll or an array of recent rolls
// params: { value, dieType, lastRolls, metadata } where lastRolls is an array of previous roll values (most recent first or last)
// metadata can include debug triggers for testing, e.g. { debug: 'forceCriticalSuccess' }
export function evaluateRoll({ value, dieType, lastRolls = [], metadata = {} } = {}) {
  // Debug: log inputs
  if (typeof console !== 'undefined' && console.log)
    console.log('[CriticalNotifier] evaluateRoll called', { value, dieType, lastRolls, metadata })

  // Module-level debug forcing: if enabled, return the forced debug result immediately
  if (typeof debugMode !== 'undefined' && debugMode) {
    // Debug: indicate forcing
    if (typeof console !== 'undefined' && console.log)
      console.log('[CriticalNotifier] debugMode active, forcing result:', debugResult)

    // Compute sides once for meta
    const forcedSides = getSidesFromDieType(dieType) || null
    const dr = String(debugResult || '').toLowerCase()
    switch (dr) {
      case 'critical_success':
      case 'success':
        if (typeof console !== 'undefined' && console.log)
          console.log('[CriticalNotifier] evaluateRoll -> forced critical_success')
        return {
          type: 'critical_success',
          message: getRandomMessage('success'),
          meta: { sides: forcedSides }
        }
      case 'critical_failure':
      case 'failure':
        if (typeof console !== 'undefined' && console.log)
          console.log('[CriticalNotifier] evaluateRoll -> forced critical_failure')
        return {
          type: 'critical_failure',
          message: getRandomMessage('failure'),
          meta: { sides: forcedSides }
        }
      case 'critical_success_streak':
        if (typeof console !== 'undefined' && console.log)
          console.log('[CriticalNotifier] evaluateRoll -> forced critical_success_streak')
        return {
          type: 'critical_success_streak',
          message: getRandomMessage('success_streak'),
          meta: {
            sides: forcedSides,
            last3: [value].concat(Array.isArray(lastRolls) ? lastRolls.slice(0, 2) : [])
          }
        }
      case 'critical_success_double':
        if (typeof console !== 'undefined' && console.log)
          console.log('[CriticalNotifier] evaluateRoll -> forced critical_success_double')
        return {
          type: 'critical_success_double',
          message: getRandomMessage('success_double'),
          meta: {
            sides: forcedSides,
            last2: [value].concat(Array.isArray(lastRolls) ? lastRolls.slice(0, 1) : [])
          }
        }
      case 'critical_failure_double':
        if (typeof console !== 'undefined' && console.log)
          console.log('[CriticalNotifier] evaluateRoll -> forced critical_failure_double')
        return {
          type: 'critical_failure_double',
          message: getRandomMessage('failure_double'),
          meta: {
            sides: forcedSides,
            last2: [value].concat(Array.isArray(lastRolls) ? lastRolls.slice(0, 1) : [])
          }
        }
      case 'critical_failure_streak':
        if (typeof console !== 'undefined' && console.log)
          console.log('[CriticalNotifier] evaluateRoll -> forced critical_failure_streak')
        return {
          type: 'critical_failure_streak',
          message: getRandomMessage('failure_streak'),
          meta: {
            sides: forcedSides,
            last3: [value].concat(Array.isArray(lastRolls) ? lastRolls.slice(0, 2) : [])
          }
        }
      default:
        if (typeof console !== 'undefined' && console.log)
          console.log('[CriticalNotifier] evaluateRoll -> forced (default) critical_success')
        return {
          type: 'critical_success',
          message: getRandomMessage('success'),
          meta: { sides: forcedSides }
        }
    }
  }

  let sides = getSidesFromDieType(dieType)
  // Fallback: if dieType wasn't provided but the roll value is a natural 20 or 1,
  // assume a d20 so natural 20/1 notifications still fire when callers omit dieType.
  if (
    (!sides || typeof sides !== 'number') &&
    typeof value === 'number' &&
    (value === 20 || value === 1)
  ) {
    sides = 20
  }
  if (!sides || typeof value !== 'number') return null
  // Reference metadata to avoid unused param lint when debug flags are used elsewhere
  void metadata

  const isNat20 = sides === 20 && value === 20
  const isNat1 = sides === 20 && value === 1

  // Check streaks if lastRolls provided (include current roll in streak test)
  const streakArray = Array.isArray(lastRolls) ? [...lastRolls] : []
  // include current value at the front
  streakArray.unshift(value)

  const last3 = streakArray.slice(0, 3)
  const threeSuccesses = last3.length === 3 && last3.every((v) => v === sides && sides === 20)
  const threeFailures = last3.length === 3 && last3.every((v) => v === 1 && sides === 20)

  if (threeSuccesses) {
    return {
      type: 'critical_success_streak',
      message: getRandomMessage('success_streak'),
      meta: { sides, last3 }
    }
  }

  if (threeFailures) {
    return {
      type: 'critical_failure_streak',
      message: getRandomMessage('failure_streak'),
      meta: { sides, last3 }
    }
  }

  // Two-in-a-row criticals (double): check previous roll only
  try {
    const prev = Array.isArray(lastRolls) && lastRolls.length > 0 ? lastRolls[0] : null
    const isPrevSuccess = sides === 20 && prev === 20
    const isPrevFailure = sides === 20 && prev === 1
    if (isPrevSuccess && isNat20) {
      return {
        type: 'critical_success_double',
        message: getRandomMessage('success_double'),
        meta: { sides, last2: [value, prev] }
      }
    }
    if (isPrevFailure && isNat1) {
      return {
        type: 'critical_failure_double',
        message: getRandomMessage('failure_double'),
        meta: { sides, last2: [value, prev] }
      }
    }
  } catch (e) {
    // Non-fatal; fall through to single checks
    if (typeof console !== 'undefined' && console.warn)
      console.warn('[CriticalNotifier] double-check failed', e)
  }

  if (isNat20) {
    return { type: 'critical_success', message: getRandomMessage('success'), meta: { sides } }
  }

  if (isNat1) {
    return { type: 'critical_failure', message: getRandomMessage('failure'), meta: { sides } }
  }

  return null
}

function getRandomMessage(kind) {
  switch (kind) {
    case 'success':
      return SUCCESS_MESSAGES[Math.floor(Math.random() * SUCCESS_MESSAGES.length)]
    case 'failure':
      return FAILURE_MESSAGES[Math.floor(Math.random() * FAILURE_MESSAGES.length)]
    case 'success_streak':
      return SUCCESS_STREAK_MESSAGES[Math.floor(Math.random() * SUCCESS_STREAK_MESSAGES.length)]
    case 'failure_streak':
      return FAILURE_STREAK_MESSAGES[Math.floor(Math.random() * FAILURE_STREAK_MESSAGES.length)]
    case 'success_double':
      return DOUBLE_SUCCESS_MESSAGES[Math.floor(Math.random() * DOUBLE_SUCCESS_MESSAGES.length)]
    case 'failure_double':
      return DOUBLE_FAILURE_MESSAGES[Math.floor(Math.random() * DOUBLE_FAILURE_MESSAGES.length)]
    default:
      return ''
  }
}

// --- Simple in-module animations (browser-only) ---
// Lightweight confetti implementation that attaches a canvas to the given container
// and runs for a short duration. Designed to be dependency-free and reusable.
export function playConfetti({
  containerId = 'global-dice-critical-notifier',
  count = 80,
  duration = 1800
} = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  try {
    // Always attach a fullscreen fixed canvas to the body so it overlays the entire app.
    const body = document.body || document.documentElement
    const canvas = document.createElement('canvas')
    canvas.className = 'critical-confetti-canvas'
    canvas.style.position = 'fixed'
    canvas.style.left = '0'
    canvas.style.top = '0'
    canvas.style.width = '100%'
    canvas.style.height = '100%'
    canvas.style.pointerEvents = 'none'
    // Very large z-index baseline to ensure it's above dialogs/canvases
    const BASE_Z = 2147483647
    canvas.style.zIndex = String(BASE_Z)
    body.appendChild(canvas)

    // Debug: confirm canvas appended and visible when debugMode enabled
    try {
      console.log('[CriticalNotifier] confetti canvas appended', canvas)
      const cs = window.getComputedStyle(canvas)
      console.log('[CriticalNotifier] confetti canvas computed style', {
        zIndex: cs.zIndex,
        position: cs.position,
        display: cs.display,
        visibility: cs.visibility,
        opacity: cs.opacity
      })
      // debug test box removed
    } catch (err) {
      console.warn('[CriticalNotifier] confetti canvas post-append check failed', err)
    }

    const ctx = canvas.getContext('2d')
    const dpi = window.devicePixelRatio || 1

    function resize() {
      canvas.width = Math.max(1, Math.floor(window.innerWidth * dpi))
      canvas.height = Math.max(1, Math.floor(window.innerHeight * dpi))
      ctx.setTransform(dpi, 0, 0, dpi, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    // Determine target rect for spawning confetti. Prefer the actual notifier element
    // (inline banner or global notifier) so confetti appears in the same stacking context.
    let targetRect = { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
    let anchorEl = null
    try {
      // Prefer visible inline notifier first, then global notifier, then explicit id, then dialog
      anchorEl = document.querySelector('.inline-notifier') || null
      if (!anchorEl) anchorEl = document.querySelector('.critical-notifier') || null
      if (!anchorEl) anchorEl = document.getElementById(containerId) || null
      if (!anchorEl) anchorEl = document.querySelector('.dice-roller-dialog') || null
      if (anchorEl) {
        targetRect = anchorEl.getBoundingClientRect()
        // compute z-index from anchor if possible and raise canvas above it
        try {
          const cs = window.getComputedStyle(anchorEl)
          const z = parseInt(cs.zIndex, 10)
          const finalZ = !Number.isNaN(z) ? Math.max(BASE_Z, z + 10) : BASE_Z
          canvas.style.zIndex = String(finalZ)
        } catch (err) {
          console.warn('[CriticalNotifier] failed to compute anchor zIndex', err)
          canvas.style.zIndex = '9999'
        }
        // If the anchor rect appears outside the current viewport or has zero height
        // (e.g. mounted offscreen or in another stacking context), adjust to a
        // sensible on-screen fallback region (top-center) so confetti is visible.
        try {
          const vpW = window.innerWidth || document.documentElement.clientWidth
          const vpH = window.innerHeight || document.documentElement.clientHeight
          const rectOutOfView =
            targetRect.top > vpH || targetRect.bottom < 0 || (targetRect.height || 0) <= 0
          if (rectOutOfView) {
            const fallbackW = Math.max(200, Math.floor(vpW * 0.6))
            const fallbackLeft = Math.max(0, Math.floor((vpW - fallbackW) / 2))
            const fallbackTop = Math.max(0, Math.floor(vpH * 0.12))
            const fallbackH = Math.max(80, Math.floor(vpH * 0.2))
            targetRect = {
              left: fallbackLeft,
              top: fallbackTop,
              width: fallbackW,
              height: fallbackH
            }
            // ensure canvas is above UI (baseline)
            canvas.style.zIndex = String(BASE_Z)
            console.log(
              '[CriticalNotifier] playConfetti adjusted targetRect to viewport fallback',
              targetRect
            )
          }
        } catch (err) {
          console.warn('[CriticalNotifier] playConfetti fallback adjust failed', err)
        }
      } else {
        console.log('[CriticalNotifier] playConfetti no anchor found, using fullscreen target')
      }
      console.log('[CriticalNotifier] playConfetti targetRect', targetRect, 'anchorEl:', anchorEl)
    } catch (err) {
      console.warn('[CriticalNotifier] playConfetti target rect failed', err)
    }

    const colors = ['#ffd700', '#ff5c5c', '#7afcff', '#9b59b6', '#4caf50']
    const pieces = []
    // If anchor height is small/zero, spawn confetti above the anchor so it falls into view
    const cw = Math.max(1, targetRect.width || window.innerWidth)
    const ch = Math.max(1, targetRect.height || Math.floor(window.innerHeight * 0.5))
    const spawnAbove = Math.max(120, Math.floor(ch * 0.5))
    // Add some spawn jitter and per-piece motion randomness for more natural look
    const spawnJitterX = Math.max(24, Math.floor(cw * 0.12))
    const spawnJitterY = Math.max(12, Math.floor(spawnAbove * 0.5))
    for (let i = 0; i < count; i++) {
      // base x,y within target rect
      const baseX = targetRect.left + Math.random() * cw
      const baseY = Math.max(0, (targetRect.top || 0) - spawnAbove) + Math.random() * spawnAbove
      // jitter a little so bursts vary
      const x = baseX + (Math.random() - 0.5) * spawnJitterX
      const y = Math.max(0, baseY + (Math.random() - 0.5) * spawnJitterY)
      const w = 6 + Math.random() * 18
      const h = 6 + Math.random() * 18
      pieces.push({
        x,
        y,
        w,
        h,
        r: Math.random() * 360,
        vx: -4 + Math.random() * 8,
        vy: 1 + Math.random() * 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotSpeed: -8 + Math.random() * 16,
        // motion randomness
        swayAmp: 0.6 + Math.random() * 3.2,
        swayFreq: 0.002 + Math.random() * 0.02,
        swayPhase: Math.random() * Math.PI * 2,
        drag: 0.985 + Math.random() * 0.02,
        gravity: 0.02 + Math.random() * 0.06
      })
    }

    let start = performance.now()
    let rafId = null

    function draw(now) {
      let elapsed = now - start
      try {
        if (typeof debugMode !== 'undefined' && debugMode && typeof console !== 'undefined')
          console.log('[CriticalNotifier] confetti draw tick', { elapsed })
        // Clear full canvas (in CSS pixels)
        ctx.clearRect(0, 0, canvas.width / dpi, canvas.height / dpi)
        for (const p of pieces) {
          // apply drag
          p.vx *= p.drag
          p.vy *= p.drag
          // gravity and sway
          p.vy += p.gravity
          // subtle horizontal sway for natural motion
          const sway =
            Math.sin((now + (p.swayPhase || 0)) * (p.swayFreq || 0.01)) * (p.swayAmp || 1)
          p.x += p.vx + sway
          p.y += p.vy
          p.r += p.rotSpeed
          ctx.save()
          ctx.translate(p.x, p.y)
          ctx.rotate((p.r * Math.PI) / 180)
          ctx.fillStyle = p.color
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
          ctx.restore()
        }
      } catch (drawErr) {
        console.warn('[CriticalNotifier] confetti draw error', drawErr)
      }
      if (elapsed < duration) {
        rafId = requestAnimationFrame(draw)
      } else {
        // fade out quickly then remove
        canvas.style.transition = 'opacity 400ms ease-out'
        canvas.style.opacity = '0'
        setTimeout(() => {
          try {
            window.removeEventListener('resize', resize)
            if (rafId) cancelAnimationFrame(rafId)
            if (canvas.parentNode) canvas.parentNode.removeChild(canvas)
          } catch (e) {
            console.warn('playConfetti cleanup error', e)
          }
        }, 450)
      }
    }

    rafId = requestAnimationFrame(draw)
    return {
      stop: () => {
        try {
          if (rafId) cancelAnimationFrame(rafId)
          if (canvas.parentNode) canvas.parentNode.removeChild(canvas)
        } catch (err) {
          console.warn('playConfetti stop cleanup error', err)
        }
      }
    }
  } catch (e) {
    console.warn('playConfetti failed', e)
    return { stop: () => {} }
  }
}

// Simple fireworks-style burst animation
export function playFireworks({
  containerId = 'global-dice-critical-notifier',
  count = 24,
  duration = 1600
} = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  try {
    const body = document.body || document.documentElement
    const canvas = document.createElement('canvas')
    canvas.className = 'critical-fireworks-canvas'
    canvas.style.position = 'fixed'
    canvas.style.left = '0'
    canvas.style.top = '0'
    canvas.style.width = '100%'
    canvas.style.height = '100%'
    canvas.style.pointerEvents = 'none'
    const BASE_Z = 2147483647
    canvas.style.zIndex = String(BASE_Z)
    body.appendChild(canvas)

    const ctx = canvas.getContext('2d')
    const dpi = window.devicePixelRatio || 1
    let sideTimerLeft = null
    let sideTimerRight = null
    let fadeTimeout = null
    function resize() {
      canvas.width = Math.max(1, Math.floor(window.innerWidth * dpi))
      canvas.height = Math.max(1, Math.floor(window.innerHeight * dpi))
      ctx.setTransform(dpi, 0, 0, dpi, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    // Find anchor element and adjust canvas z-index to match overlay stacking
    let anchorEl = null
    try {
      anchorEl = document.querySelector('.inline-notifier') || null
      if (!anchorEl) anchorEl = document.querySelector('.critical-notifier') || null
      if (!anchorEl) anchorEl = document.getElementById(containerId) || null
      if (!anchorEl) anchorEl = document.querySelector('.dice-roller-dialog') || null
      if (anchorEl) {
        try {
          const cs = window.getComputedStyle(anchorEl)
          const z = parseInt(cs.zIndex, 10)
          const finalZ = !Number.isNaN(z) ? Math.max(BASE_Z, z + 10) : BASE_Z
          canvas.style.zIndex = String(finalZ)
        } catch (err) {
          console.warn('[CriticalNotifier] fireworks failed to compute anchor zIndex', err)
          canvas.style.zIndex = String(BASE_Z)
        }
      }
    } catch (err) {
      console.warn('[CriticalNotifier] fireworks anchor detection failed', err)
    }

    // determine origin (center of anchor or top-center fallback)
    let origin = {
      x: window.innerWidth / 2,
      y: Math.max(80, Math.floor(window.innerHeight * 0.18))
    }
    try {
      if (anchorEl) {
        const r = anchorEl.getBoundingClientRect()
        const vpW = window.innerWidth || document.documentElement.clientWidth
        const vpH = window.innerHeight || document.documentElement.clientHeight
        const rectOutOfView = r.top > vpH || r.bottom < 0 || (r.height || 0) <= 0
        if (!rectOutOfView && ((r.width || 0) > 0 || (r.height || 0) > 0)) {
          try {
            // use r/vpW/vpH from the outer scope
            if (!rectOutOfView && ((r.width || 0) > 0 || (r.height || 0) > 0)) {
              origin = {
                x: Math.floor(r.left + (r.width || 0) / 2),
                y: Math.floor((r.top || 0) + (r.height || 0) / 2)
              }
            } else {
              // fallback to top-center but keep canvas z-index high
              canvas.style.zIndex = String(BASE_Z)
              origin = { x: Math.floor(vpW / 2), y: Math.max(80, Math.floor(vpH * 0.18)) }
              if (typeof console !== 'undefined' && debugMode)
                console.log(
                  '[CriticalNotifier] fireworks origin fallback used',
                  origin,
                  'anchorRect:',
                  r
                )
            }
          } catch (e) {
            if (typeof console !== 'undefined' && console.warn)
              console.warn('[CriticalNotifier] fireworks origin calc failed', e)
          }

          // add small random jitter to origin so bursts aren't always exactly centered
          try {
            const originJitterX = Math.max(12, Math.floor(window.innerWidth * 0.04))
            const originJitterY = Math.max(8, Math.floor(window.innerHeight * 0.03))
            origin.x = Math.max(
              8,
              Math.min(window.innerWidth - 8, origin.x + (Math.random() - 0.5) * originJitterX)
            )
            origin.y = Math.max(
              8,
              Math.min(window.innerHeight - 8, origin.y + (Math.random() - 0.5) * originJitterY)
            )
          } catch {
            /* ignore jitter errors */
          }
        }
      }
    } catch (e) {
      if (typeof console !== 'undefined' && console.warn)
        console.warn('[CriticalNotifier] fireworks origin calc failed', e)
    }

    const colors = ['#ffd700', '#ff5c5c', '#7afcff', '#9b59b6', '#4caf50', '#ff9f1c']
    const particles = []

    // Helper to create a burst of particles at a given center
    function createBurst(cx, cy, cnt, opts = {}) {
      const burstCount = Math.max(1, Math.floor(cnt))
      for (let i = 0; i < burstCount; i++) {
        const angle = Math.random() * Math.PI * 2
        const speed = (opts.speedBias || 1) * (1.5 + Math.random() * 7)
        const radialAccel =
          typeof opts.radialAccel === 'number' ? opts.radialAccel : 0.01 + Math.random() * 0.04
        particles.push({
          x: cx + (Math.random() - 0.5) * 8,
          y: cy + (Math.random() - 0.5) * 8,
          vx: Math.cos(angle) * speed,
          // bias initial vertical speed slightly upward for more natural bursts
          vy: Math.sin(angle) * speed * (opts.vyBias || -0.6),
          life: 0,
          ttl: 300 + Math.random() * (opts.ttlExtra || 900),
          size: (opts.sizeBias || 1) * (1.5 + Math.random() * 4),
          color: colors[Math.floor(Math.random() * colors.length)],
          drag: 0.985 + Math.random() * 0.02,
          gravity: 0.02 + Math.random() * 0.08,
          radialAccel
        })
      }
    }

    // Main central burst
    createBurst(origin.x, origin.y, count, { vyBias: -0.6 })

    // Create two smaller, randomized bursts left and right of the main origin
    try {
      const sideOffset = Math.max(60, Math.floor(window.innerWidth * 0.12))
      const jitter = Math.max(12, Math.floor(window.innerWidth * 0.03))
      const leftX = Math.max(8, origin.x - sideOffset + (Math.random() - 0.5) * jitter)
      const rightX = Math.min(
        window.innerWidth - 8,
        origin.x + sideOffset + (Math.random() - 0.5) * jitter
      )
      const sideY = Math.max(
        40,
        origin.y + (Math.random() - 0.5) * Math.floor(window.innerHeight * 0.04)
      )
      const smallCount = Math.max(6, Math.floor(count * 0.28))
      // schedule left and right bursts with a small stagger so they don't appear simultaneously
      const delayLeft = 80 + Math.floor(Math.random() * 160)
      const delayRight = delayLeft + 60 + Math.floor(Math.random() * 120)
      sideTimerLeft = setTimeout(() => {
        try {
          createBurst(leftX, sideY, smallCount, { speedBias: 0.9, sizeBias: 0.85, ttlExtra: 600 })
        } catch (innerE) {
          if (typeof console !== 'undefined' && console.warn)
            console.warn('[CriticalNotifier] left burst failed', innerE)
        }
      }, delayLeft)
      sideTimerRight = setTimeout(() => {
        try {
          createBurst(rightX, sideY, smallCount, { speedBias: 1.1, sizeBias: 0.95, ttlExtra: 700 })
        } catch (innerE) {
          if (typeof console !== 'undefined' && console.warn)
            console.warn('[CriticalNotifier] right burst failed', innerE)
        }
      }, delayRight)
    } catch (e) {
      // if layout measurements fail, just skip side bursts
      if (typeof console !== 'undefined' && console.warn)
        console.warn('[CriticalNotifier] side bursts creation failed', e)
    }

    let start = performance.now()
    let rafId = null

    function draw(now) {
      const elapsed = now - start
      try {
        if (typeof debugMode !== 'undefined' && debugMode && typeof console !== 'undefined')
          console.log('[CriticalNotifier] fireworks draw tick', {
            elapsed,
            particles: particles.length,
            origin
          })
        ctx.clearRect(0, 0, canvas.width / dpi, canvas.height / dpi)
        for (const p of particles) {
          p.life += 16
          // drag
          p.vx *= p.drag
          p.vy *= p.drag
          // radial acceleration: apply along the normalized radial vector so particles
          // accelerate outward (away from the origin). Use a safe distance to avoid div by 0.
          try {
            const dx = p.x - origin.x
            const dy = p.y - origin.y
            const dist = Math.hypot(dx, dy) || 1
            const ax = (p.radialAccel * dx) / dist
            const ay = (p.radialAccel * dy) / dist
            p.vx += ax
            p.vy += ay
          } catch {
            // fallback: small outward push based on current vx/vy signs
            p.vx += p.radialAccel * (p.vx >= 0 ? 0.5 : -0.5)
            p.vy += p.radialAccel * (p.vy >= 0 ? 0.5 : -0.5)
          }
          // gravity pulls downward (positive y is down in canvas)
          p.vy += p.gravity
          p.x += p.vx
          p.y += p.vy
          const alpha = Math.max(0, 1 - p.life / p.ttl)
          ctx.beginPath()
          ctx.fillStyle = p.color
          ctx.globalAlpha = alpha
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
          ctx.fill()
          ctx.closePath()
          // trail
          if (alpha > 0.12) {
            ctx.globalAlpha = alpha * 0.5
            ctx.fillRect(p.x - 0.5, p.y - 0.5, 1, 1)
          }
        }
        ctx.globalAlpha = 1
      } catch (err) {
        console.warn('[CriticalNotifier] fireworks draw error', err)
      }

      if (elapsed < duration) {
        rafId = requestAnimationFrame(draw)
      } else {
        canvas.style.transition = 'opacity 300ms ease-out'
        canvas.style.opacity = '0'
        fadeTimeout = setTimeout(() => {
          try {
            window.removeEventListener('resize', resize)
            if (rafId) cancelAnimationFrame(rafId)
            if (sideTimerLeft) clearTimeout(sideTimerLeft)
            if (sideTimerRight) clearTimeout(sideTimerRight)
            if (canvas.parentNode) canvas.parentNode.removeChild(canvas)
          } catch (e) {
            console.warn('playFireworks cleanup error', e)
          }
        }, 350)
      }
    }

    rafId = requestAnimationFrame(draw)
    return {
      stop: () => {
        try {
          if (rafId) cancelAnimationFrame(rafId)
          if (sideTimerLeft) clearTimeout(sideTimerLeft)
          if (sideTimerRight) clearTimeout(sideTimerRight)
          if (fadeTimeout) clearTimeout(fadeTimeout)
          try {
            window.removeEventListener('resize', resize)
          } catch {
            /* ignore */
          }
          if (canvas.parentNode) canvas.parentNode.removeChild(canvas)
        } catch (err) {
          console.warn('playFireworks stop cleanup error', err)
        }
      }
    }
  } catch (e) {
    console.warn('playFireworks failed', e)
    return { stop: () => {} }
  }
}

export function triggerAnimation(type, opts = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  switch (String(type || '').toLowerCase()) {
    case 'critical_success':
    case 'success':
      return playConfetti(opts)
    case 'critical_success_streak':
      // For 3x criticals trigger both confetti + fireworks twice (staggered)
      try {
        const c1 = playConfetti({
          ...opts,
          count: opts.count || 140,
          duration: opts.duration || 2600
        })
        const f1 = playFireworks({
          ...opts,
          count: opts.count || 160,
          duration: opts.duration || 2600
        })
        let c2 = null
        let f2 = null
        const staggerMs = 320
        const timer = setTimeout(() => {
          try {
            c2 = playConfetti({
              ...opts,
              count: opts.count || 120,
              duration: opts.duration || 2000
            })
            f2 = playFireworks({
              ...opts,
              count: opts.count || 120,
              duration: opts.duration || 2000
            })
          } catch (err2) {
            console.warn('[CriticalNotifier] staggered combined animation failed', err2)
          }
        }, staggerMs)

        return {
          stop: () => {
            try {
              clearTimeout(timer)
            } catch (errClear) {
              if (typeof debugMode !== 'undefined' && debugMode && typeof console !== 'undefined')
                console.warn('[CriticalNotifier] failed to clear stagger timer', errClear)
            }
            try {
              if (c1 && c1.stop) c1.stop()
            } catch (e) {
              console.warn('combined stop confetti1 failed', e)
            }
            try {
              if (f1 && f1.stop) f1.stop()
            } catch (e) {
              console.warn('combined stop fireworks1 failed', e)
            }
            try {
              if (c2 && c2.stop) c2.stop()
            } catch (e) {
              console.warn('combined stop confetti2 failed', e)
            }
            try {
              if (f2 && f2.stop) f2.stop()
            } catch (e) {
              console.warn('combined stop fireworks2 failed', e)
            }
          }
        }
      } catch (err) {
        console.warn('[CriticalNotifier] combined animation fallback to confetti failed', err)
        return playConfetti({ ...opts, count: opts.count || 140, duration: opts.duration || 2600 })
      }
    case 'critical_success_double':
      // double triggers fireworks
      return playFireworks({ ...opts, count: opts.count || 110, duration: opts.duration || 2400 })
    case 'critical_failure_double':
      // double failure -> fireworks as well (emphasis)
      return playFireworks({ ...opts, count: opts.count || 60, duration: opts.duration || 2000 })
    case 'critical_failure':
    case 'failure':
      // Simple shake or red flash could be added; for now, no-op
      return { stop: () => {} }
    default:
      return { stop: () => {} }
  }
}

// Notify helper dispatches a window CustomEvent that UI components can listen to
import { useCriticalNotifierStore } from '@/stores/criticalNotifierStore'

// Manual triggering for testing removed; use `emitNotification` from `useCriticalNotifierStore()`
// or call `processAndNotify()`/`evaluateRoll()` from code with desired parameters.

// Convenience function: evaluate and notify if a critical condition occurred
// Accepts optional metadata and rollType; persists recent rolls and emits via Pinia store
export async function processAndNotify({
  value,
  dieType,
  lastRolls = [],
  rollType = null,
  metadata = {}
} = {}) {
  // Debug: log invocation
  if (typeof console !== 'undefined' && console.log)
    console.log('[CriticalNotifier] processAndNotify called', {
      value,
      dieType,
      lastRolls,
      rollType,
      metadata
    })

  // Pass metadata through so evaluateRoll can act on debug flags
  const result = evaluateRoll({ value, dieType, lastRolls, metadata })
  if (typeof console !== 'undefined' && console.log)
    console.log('[CriticalNotifier] evaluateRoll returned', result)
  if (!result) return null

  try {
    // Use Pinia store to emit notification and persist recent rolls
    const notifierStore = useCriticalNotifierStore()

    // Build a compact recent roll entry
    const entry = {
      value,
      dieType,
      lastRolls: Array.isArray(lastRolls) ? lastRolls.slice(0, 5) : [],
      rollType,
      metadata
    }

    // Persist recent roll (async, non-blocking)
    try {
      await notifierStore.addRecentRoll(entry)
    } catch (e) {
      console.warn('Failed to persist recent roll for notifier', e)
    }

    // Debug log the payload so devs can confirm notifier invocation
    if (typeof console !== 'undefined' && console.log)
      console.log('[CriticalNotifier] emitting payload', { result, rollType, metadata })

    // Emit notification (reactive) so UI components can react
    notifierStore.emitNotification({ ...result, context: { rollType, metadata } })
    // Play contextual animation (non-blocking) for certain notification types
    try {
      triggerAnimation(result.type, { containerId: 'global-dice-critical-notifier' })
    } catch (e) {
      console.warn('Notifier triggerAnimation failed', e)
    }
    if (typeof console !== 'undefined' && console.log)
      console.log('[CriticalNotifier] emitNotification called')
  } catch (e) {
    console.warn('Notifier processAndNotify failed', e)
  }

  return result
}
